import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { MouseEvent as ReactMausEreignis, PointerEvent as ReactZeigerEreignis } from "react";
import { createPortal } from "react-dom";
import {
  altersText,
  datumAusSpalten,
  formatDatum,
  lebensalter,
  type UnscharfesDatum,
} from "@stammbaum/shared";
import { bildUrl, type PersonKurz } from "../api";
import type { Umfeld } from "./umfeld";

/**
 * Die Infokarte zu einer Person — beim Überfahren mit der Maus, am
 * Handy nach langem Drücken.
 *
 * Sie stellt KEINE Anfrage. Alles, was hier steht, liegt schon im
 * Speicher: der Baum und die Verwandtschaftsliste bekommen pro Person
 * beide Daten vollständig (mit Genauigkeit, also auch „um 1750"),
 * Kurzinfo, Bezeichnung und Entfernung; Eltern, Partner und Kinderzahl
 * rechnet `umfeldKarte` aus den mitgelieferten Kanten. Was nicht da ist,
 * fehlt in der Karte einfach — eine Zeile, die lädt, wäre genau das, was
 * hier nicht passieren soll.
 *
 * Die Karte hängt per Portal am <body>. Im Baum steckt der Auslöser in
 * einem SVG mit eigener Verschiebung und Vergrößerung; ein Kästchen
 * innerhalb dieses Koordinatensystems wäre mitskaliert worden.
 */

/** Was die Karte zeigen kann. Alles ausser dem Namen ist freiwillig. */
export interface KartenPerson extends Partial<PersonKurz> {
  id: number;
  vorname: string;
  famname: string;
}

const BREITE = 288;
const RAND = 8;
/** Verzögerung mit der Maus, damit die Karte beim Vorbeifahren nicht aufblitzt. */
const MAUS_MS = 300;
/** So lange muss der Finger liegen bleiben. */
const DRUECKEN_MS = 500;
/** Bewegt sich der Finger weiter, war es Scrollen oder Schieben. */
const WACKELN_PX = 10;

function datum(p: KartenPerson, art: "geburt" | "tod"): UnscharfesDatum | null {
  return datumAusSpalten(
    {
      geburtVon: p.geburtVon ?? null,
      geburtBis: p.geburtBis ?? null,
      geburtGenauigkeit: p.geburtGenauigkeit ?? null,
      geburtText: p.geburtText ?? null,
      todVon: p.todVon ?? null,
      todBis: p.todBis ?? null,
      todGenauigkeit: p.todGenauigkeit ?? null,
      todText: p.todText ?? null,
    },
    art,
  );
}

/**
 * Der Abstand in Worten.
 *
 * `bezeichnung` sagt „Cousine", diese Zeile sagt, wie weit das ist. Beides
 * zusammen beantwortet die Frage, die man vor einem fremden Namen im Baum
 * wirklich hat.
 */
function abstandText(e: NonNullable<PersonKurz["entfernung"]>): string {
  const schritte = (n: number) => `${n} ${n === 1 ? "Schritt" : "Schritte"}`;
  const teile: string[] = [];

  if (e.blut > 0) {
    const richtung =
      e.aufwaerts > 0 && e.abwaerts === 0
        ? " hinauf"
        : e.abwaerts > 0 && e.aufwaerts === 0
          ? " hinunter"
          : "";
    teile.push(`${schritte(e.blut)} im Blut${richtung}`);
  }
  if (e.schwieger > 0) teile.push(`${schritte(e.schwieger)} angeheiratet`);
  return teile.join(" · ");
}

/**
 * Hat der Server den Datensatz gekürzt?
 *
 * Gekürzt heisst: bei einer lebenden Person bleiben nur Name, Geschlecht
 * und Geburtsjahr. Ohne einen Hinweis darauf sieht eine fast leere Karte
 * nach einem Fehler aus, und man sucht ihn in der Anwendung statt in den
 * Berechtigungen.
 *
 * Erkennbar ist es am fehlenden `geburtBis`: ein echtes Jahresdatum hat
 * immer beide Grenzen (1990-01-01 bis 1990-12-31), weil `parseDatum` sie
 * so schreibt. Die Kürzung setzt nur `von` auf den 1. Jänner. Ein
 * lebender Verwandter, den man ganz sehen darf, faellt damit nicht
 * faelschlich unter diesen Hinweis. Das ist ein Rueckschluss auf das
 * Format und keine Auskunft des Servers - er steuert nichts, er erklaert
 * nur.
 */
function istGekuerzt(p: KartenPerson): boolean {
  return Boolean(
    p.giltAlsLebend &&
      !p.geburtBis &&
      p.geburtGenauigkeit === "jahr" &&
      !p.todVon &&
      !p.kurzinfo,
  );
}

/**
 * Lohnt sich die Karte überhaupt?
 *
 * In der Trefferliste steht der Name schon da. Eine Karte, die nur ihn
 * wiederholt, ist kein Gewinn, sondern ein Kästchen, das beim Lesen im
 * Weg steht.
 */
function lohntSich(p: KartenPerson, umfeld?: Umfeld): boolean {
  if (p.kurzinfo || p.bezeichnung || p.entfernung || p.rufname || p.maedchenname) return true;
  if (p.geburtVon || p.geburtBis || p.geburtText || p.todVon || p.todBis || p.todText) return true;
  if (umfeld && (umfeld.elternIds.length > 0 || umfeld.partnerIds.length > 0 || umfeld.kinder > 0))
    return true;
  return false;
}

function Inhalt({
  p,
  umfeld,
  namen,
  hinweis,
}: {
  p: KartenPerson;
  umfeld?: Umfeld;
  namen?: (id: number) => string | null;
  hinweis?: string;
}) {
  const geburt = datum(p, "geburt");
  const tod = datum(p, "tod");
  const alter = lebensalter(geburt, tod, p.giltAlsLebend ?? false);
  const alterText = altersText(alter);

  const gekuerzt = istGekuerzt(p);
  const abstand = p.entfernung ? abstandText(p.entfernung) : "";

  const nenne = (ids: number[]) =>
    ids
      .map((id) => namen?.(id))
      .filter((n): n is string => Boolean(n));

  const eltern = umfeld ? nenne(umfeld.elternIds) : [];
  const partner = umfeld ? nenne(umfeld.partnerIds) : [];

  const zweitname = [
    p.rufname ? `genannt ${p.rufname}` : null,
    p.maedchenname ? `geborene ${p.maedchenname}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <div className="flex items-start gap-2">
        {p.portrait && (
          <img
            // 160, nicht 64: das Bild ist 44 Pixel breit, auf einem
            // feinen Bildschirm also 88 - mit 64 waere es weich.
            src={bildUrl(p.portrait.medienId, 160, p.portrait.tagId)}
            alt=""
            className="h-11 w-11 shrink-0 rounded-full object-cover"
          />
        )}
        <div className="min-w-0">
          <p className="font-medium leading-tight">
            {p.vorname} {p.famname}
          </p>
          {zweitname && (
            <p className="text-xs text-black/50 dark:text-white/50">{zweitname}</p>
          )}
        </div>
      </div>

      {(geburt || tod || alterText) && (
        <div className="mt-2 space-y-0.5">
          {geburt && <p>* {formatDatum(geburt)}</p>}
          {tod && (
            <p>
              † {formatDatum(tod)}
              {alterText && (
                <span className="text-black/50 dark:text-white/50"> · mit {alterText}</span>
              )}
            </p>
          )}
          {!tod && alterText && (
            <p className="text-black/50 dark:text-white/50">{alterText} alt</p>
          )}
        </div>
      )}

      {p.kurzinfo && <p className="mt-2">{p.kurzinfo}</p>}

      {/*
        Auf den TEXT pruefen, nicht auf das Objekt: die Wurzel bekommt vom
        Server ein `entfernung` mit lauter Nullen, und daraus wird ein
        leerer Text - sonst stuende dort „diese Person · " mit nichts
        dahinter.
      */}
      {(p.bezeichnung || abstand) && (
        <p className="mt-2 border-t border-black/10 pt-2 dark:border-white/10">
          {p.bezeichnung}
          {p.bezeichnung && abstand && " · "}
          {abstand && <span className="text-black/50 dark:text-white/50">{abstand}</span>}
        </p>
      )}

      {/*
        Nur bei vollstaendig geladenen Kanten. Am Rand des geladenen
        Umkreises fehlen die Nachbarn der Nachbarn: dort stuende „Eltern:
        Maria" bei einem Paar und „keine Kinder" bei vier - beides liest
        sich als Aussage, ist aber bloss der Rand der Abfrage.
      */}
      {umfeld?.vollstaendig && (eltern.length > 0 || partner.length > 0 || umfeld.kinder > 0) && (
        <p className="mt-1 text-black/50 dark:text-white/50">
          {[
            eltern.length > 0 ? `Eltern: ${eltern.join(" und ")}` : null,
            partner.length > 0 ? `⚭ ${partner.join(", ")}` : null,
            umfeld.kinder > 0
              ? `${umfeld.kinder} ${umfeld.kinder === 1 ? "Kind" : "Kinder"}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      )}

      {gekuerzt && (
        <p className="mt-2 border-t border-black/10 pt-2 text-xs text-black/40 dark:border-white/10 dark:text-white/40">
          Lebende Person — sichtbar sind nur Name und Geburtsjahr.
        </p>
      )}

      {hinweis && (
        <p className="mt-2 border-t border-black/10 pt-2 text-xs text-black/40 dark:border-white/10 dark:text-white/40">
          {hinweis}
        </p>
      )}
    </>
  );
}

interface Offen {
  p: KartenPerson;
  umfeld?: Umfeld;
  hinweis?: string;
  rechteck: DOMRect;
}

/** Was an das auslösende Element gehängt wird. */
export interface Ausloeser {
  onPointerEnter: (e: ReactZeigerEreignis) => void;
  onPointerLeave: () => void;
  onPointerDown: (e: ReactZeigerEreignis) => void;
  onPointerMove: (e: ReactZeigerEreignis) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onClick: (e: ReactMausEreignis) => void;
  onContextMenu: (e: ReactMausEreignis) => void;
  "data-karte": string;
}

/**
 * Einmal je Liste aufrufen, nicht je Zeile: so ist immer höchstens eine
 * Karte offen, und die Zeilen selbst bleiben zustandslos. (In einer
 * Schleife ginge ein Hook ohnehin nicht.)
 */
export function usePersonKarte(namen?: (id: number) => string | null) {
  const [offen, setOffen] = useState<Offen | null>(null);
  const [pos, setPos] = useState<{ links: number; oben: number; maxHoehe: number } | null>(null);
  const kartenRef = useRef<HTMLDivElement>(null);

  const uhr = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  // Nach langem Drücken darf der folgende Klick nicht auch noch zur
  // Person springen - sonst ist die Karte im selben Augenblick wieder weg.
  const langGedrueckt = useRef(false);

  const stoppen = () => {
    if (uhr.current) clearTimeout(uhr.current);
    uhr.current = null;
    start.current = null;
  };

  const schliessen = useCallback(() => {
    stoppen();
    // Auch das Flag: sonst schluckt der naechste echte Tipp seinen Klick.
    // Es wird nur in onClick zurueckgesetzt, und nach „langes Druecken,
    // dann daneben tippen" kommt auf dem Ausloeser gar kein Klick mehr.
    langGedrueckt.current = false;
    setOffen(null);
    setPos(null);
  }, []);

  // Ein laufender Timer darf die abgehaengte Komponente nicht ueberleben.
  useEffect(() => stoppen, []);

  // Beim Scrollen, Drehen oder Tippen daneben ist die Karte am falschen
  // Platz, noch bevor jemand sie liest.
  useEffect(() => {
    if (!offen) return;
    const daneben = (e: Event) => {
      if (!kartenRef.current?.contains(e.target as Node)) schliessen();
    };
    const taste = (e: KeyboardEvent) => e.key === "Escape" && schliessen();
    document.addEventListener("pointerdown", daneben, true);
    document.addEventListener("keydown", taste);
    window.addEventListener("scroll", schliessen, true);
    window.addEventListener("resize", schliessen);
    return () => {
      document.removeEventListener("pointerdown", daneben, true);
      document.removeEventListener("keydown", taste);
      window.removeEventListener("scroll", schliessen, true);
      window.removeEventListener("resize", schliessen);
    };
  }, [offen, schliessen]);

  /*
   * Erst messen, dann setzen: ohne die Höhe weiss niemand, ob die Karte
   * unter den Auslöser passt oder darüber muss.
   *
   * Entscheidend ist, dass sie den Auslöser NIE überdeckt. Sie liegt
   * sonst unter dem Mauszeiger, der Auslöser bekommt sein
   * `pointerleave`, die Karte schliesst, der Zeiger steht wieder auf der
   * Zeile — und das Ganze von vorn. Deshalb wird nie über die Kante
   * hinaus verschoben, sondern die Höhe begrenzt und notfalls im
   * Kästchen gescrollt.
   */
  useLayoutEffect(() => {
    if (!offen) return;
    const k = kartenRef.current;
    if (!k) return;
    const hoehe = k.offsetHeight;
    const r = offen.rechteck;
    const platzUnten = window.innerHeight - r.bottom - 2 * RAND;
    const platzOben = r.top - 2 * RAND;

    const unten = platzUnten >= hoehe || platzUnten >= platzOben;
    const maxHoehe = Math.max(80, unten ? platzUnten : platzOben);
    const oben = unten ? r.bottom + RAND : Math.max(RAND, r.top - Math.min(hoehe, maxHoehe) - RAND);

    const breite = Math.min(BREITE, window.innerWidth - 2 * RAND);
    const links = Math.max(RAND, Math.min(r.left, window.innerWidth - breite - RAND));
    setPos({ links, oben, maxHoehe });
  }, [offen]);

  /** Gibt zurueck, ob tatsaechlich etwas aufgegangen ist. */
  const oeffnen = (ziel: Element, p: KartenPerson, umfeld?: Umfeld, hinweis?: string) => {
    if (!lohntSich(p, umfeld) && !hinweis) return false;
    setOffen({ p, umfeld, hinweis, rechteck: ziel.getBoundingClientRect() });
    return true;
  };

  function fuer(
    p: KartenPerson,
    opt: { umfeld?: Umfeld; hinweis?: string; beiKlick?: () => void } = {},
  ): Ausloeser {
    return {
      onPointerEnter: (e) => {
        if (e.pointerType !== "mouse") return;
        const ziel = e.currentTarget;
        stoppen();
        uhr.current = setTimeout(() => oeffnen(ziel, p, opt.umfeld, opt.hinweis), MAUS_MS);
      },
      onPointerLeave: () => {
        stoppen();
        // Nur die Maus schliesst beim Verlassen. Nach langem Drücken soll
        // die Karte stehen bleiben, bis jemand woanders hintippt.
        setOffen((o) => (o && !langGedrueckt.current ? null : o));
      },
      onPointerDown: (e) => {
        if (e.pointerType === "mouse") return;
        const ziel = e.currentTarget;
        stoppen();
        langGedrueckt.current = false;
        start.current = { x: e.clientX, y: e.clientY };
        uhr.current = setTimeout(() => {
          // Erst wenn wirklich etwas aufgeht, darf der folgende Klick
          // verschluckt werden - sonst frisst ein langer Druck auf eine
          // Person ohne Zusatzangaben den Tipp, ohne etwas zu zeigen.
          if (!oeffnen(ziel, p, opt.umfeld, opt.hinweis)) return;
          langGedrueckt.current = true;
          // Beim langen Drücken markiert der Browser gern Text mit.
          document.getSelection()?.removeAllRanges();
        }, DRUECKEN_MS);
      },
      onPointerMove: (e) => {
        if (!start.current) return;
        const weg =
          Math.abs(e.clientX - start.current.x) + Math.abs(e.clientY - start.current.y);
        if (weg > WACKELN_PX) stoppen();
      },
      onPointerUp: stoppen,
      onPointerCancel: stoppen,
      onClick: (e) => {
        if (langGedrueckt.current) {
          // preventDefault hält den Link auf, stopPropagation den Knoten
          // im Baum, der auf den Klick hin die Wurzel wechselt.
          e.preventDefault();
          e.stopPropagation();
          langGedrueckt.current = false;
          return;
        }
        schliessen();
        opt.beiKlick?.();
      },
      onContextMenu: (e) => {
        // Ab dem Aufsetzen des Fingers, nicht erst nach den 500 ms: das
        // Kontextmenü des Browsers kommt bei etwa derselben Schwelle, und
        // ein paar Millisekunden frueher stuende „Link öffnen in …" ueber
        // der Karte.
        if (start.current || langGedrueckt.current) e.preventDefault();
      },
      "data-karte": "",
    };
  }

  const karte =
    offen &&
    createPortal(
      <div
        ref={kartenRef}
        role="tooltip"
        style={{
          position: "fixed",
          left: pos?.links ?? -9999,
          top: pos?.oben ?? -9999,
          width: Math.min(BREITE, window.innerWidth - 2 * RAND),
          maxHeight: pos?.maxHoehe,
          overflowY: "auto",
          opacity: pos ? 1 : 0,
          /*
           * Die Karte ist zum Lesen da, nicht zum Anfassen: ohne das
           * finge sie die Zeigerereignisse des Auslösers ab. Der Preis
           * ist, dass sich ein `maxHeight` nicht scrollen laesst - das
           * greift aber erst auf einem sehr flachen Fenster, und dort
           * ist Abschneiden das kleinere Übel, als den halben Bildschirm
           * zuzudecken.
           */
          pointerEvents: "none",
        }}
        className="z-50 rounded-xl border border-black/10 bg-white p-3 text-sm shadow-xl
                   dark:border-white/15 dark:bg-neutral-800"
      >
        <Inhalt p={offen.p} umfeld={offen.umfeld} namen={namen} hinweis={offen.hinweis} />
      </div>,
      document.body,
    );

  return { fuer, karte, schliessen };
}
